import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { api } from "@/lib/api";
import { Loader2, Send, MessageSquare, Sparkles } from "lucide-react";
import { runJob } from "@/lib/jobs";
import { toast } from "sonner";

export default function ChatArena() {
  const [models, setModels] = useState([]);
  const [modes, setModes] = useState([]);
  const [mode, setMode] = useState("default");
  const [selected, setSelected] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    api.get("/chat-arena/models").then(r => {
      setModels(r.data);
      setSelected(r.data.slice(0, 3).map(m => m.id));
    });
    api.get("/chat-arena/modes").then(r => setModes(r.data));
  }, []);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const send = async () => {
    if (!prompt.trim() || selected.length === 0) return;
    setLoading(true);
    const currentPrompt = prompt;
    setPrompt("");
    try {
      // Async job pattern — kickoff returns immediately, we poll for the result.
      const result = await runJob("chat", {
        prompt: currentPrompt,
        models: selected,
        session_id: sessionId,
        mode,
      });
      setSessionId(result.session_id);
      setHistory(h => [...h, { prompt: currentPrompt, results: result.results, mode }]);
    } catch (err) {
      console.error("Chat arena request failed:", err);
      toast.error(err?.message || "Chat request failed. Please try again.");
    } finally { setLoading(false); }
  };

  const modelById = Object.fromEntries(models.map(m => [m.id, m]));

  return (
    <Layout hideRightPanel>
      <PageHero
        testId="chat-arena-header"
        eyebrow="Multi-Model Chat Arena"
        title={<>Ask them all. <span className="italic">Compare answers.</span></>}
        sub="Side-by-side responses from GPT-5.4, Claude Sonnet 4.5, Gemini 3 and more. Pick your favorites; ship the best answer."
      />

      <div className="curio-card p-4 mb-4">
        <div className="text-xs uppercase tracking-widest font-bold text-[#5B6478] mb-2">Active models ({selected.length})</div>
        <div className="flex flex-wrap gap-2 mb-4">
          {models.map(m => (
            <button key={m.id} onClick={() => toggle(m.id)}
              className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5 ${selected.includes(m.id) ? 'bg-[#0B1220] text-white border-[#0B1220]' : 'border-[#E3E8F0] hover:border-[#0B1220]'}`}
              data-testid={`model-toggle-${m.id.replace(/[/.]/g, '-')}`}>
              <span className="w-2 h-2 rounded-full" style={{ background: m.color }} />{m.name}
            </button>
          ))}
        </div>
        <div className="text-xs uppercase tracking-widest font-bold text-[#5B6478] mb-2">Assistance mode</div>
        <div className="flex flex-wrap gap-2">
          {modes.map(md => (
            <button key={md.id} onClick={()=>setMode(md.id)} className={`text-xs px-3 py-1.5 rounded-full border ${mode===md.id ? 'bg-[#F97316] text-[#0B1220] border-[#F97316]' : 'border-[#E3E8F0] hover:border-[#0B1220]'}`} data-testid={`mode-${md.id}`}>
              {md.name}
            </button>
          ))}
        </div>
      </div>

      <div className="curio-card p-4 mb-6">
        <div className="flex items-center gap-3">
          <MessageSquare size={16} className="text-[#5B6478]" />
          <input value={prompt} onChange={e => setPrompt(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
            placeholder="Ask anything — all selected models will answer in parallel…"
            className="flex-1 bg-transparent focus:outline-none text-sm"
            data-testid="arena-prompt-input" />
          <button onClick={send} disabled={loading || !prompt.trim() || selected.length === 0}
            className="btn-coral text-sm disabled:opacity-50" data-testid="arena-send-btn">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <><Send size={13} /> Ask all</>}
          </button>
        </div>
      </div>

      {history.length === 0 && !loading && (
        <div className="text-center py-16 text-[#5B6478]">
          <Sparkles size={28} className="mx-auto mb-3 text-[#F97316]" />
          <div className="font-display text-2xl italic mb-1">Start a chat.</div>
          <div className="text-sm">Every model you pick will answer your question in the same request.</div>
        </div>
      )}

      <div className="space-y-8" data-testid="arena-history">
        {history.map((turn, i) => (
          <div key={i}>
            <div className="bg-[#F7F9FC] rounded-2xl p-4 mb-3 text-sm">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#5B6478] mb-1">You asked</div>
              {turn.prompt}
            </div>
            <div className={`grid gap-4 ${turn.results.length === 1 ? 'grid-cols-1' : turn.results.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
              {turn.results.map((r, j) => (
                <div key={j} className="curio-card p-4">
                  <div className="flex items-center gap-2 mb-2 pb-2 border-b border-[#E3E8F0]">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: modelById[r.model]?.color || '#5B6478' }} />
                    <span className="font-semibold text-sm">{modelById[r.model]?.name || r.model}</span>
                  </div>
                  {r.error ? (
                    <p className="text-xs text-rose-600">Error: {r.error}</p>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{r.response}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {selected.map(id => <div key={id} className="skeleton h-40" />)}
          </div>
        )}
      </div>
    </Layout>
  );
}
